import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Gift,
  Info,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

const getCurrentFinancialYear = () => {
  const now = new Date();
  const calendarYear = now.getFullYear();
  const calendarMonth = now.getMonth() + 1;
  const startYear = calendarMonth >= 4 ? calendarYear : calendarYear - 1;
  return {
    label: `${startYear}-${String(startYear + 1).slice(-2)}`,
    firstDay: `${startYear}-04-01`,
    lastDay: `${startYear + 1}-03-31`,
  };
};

const formatDate = (value) => {
  if (!value) return '--';
  return new Date(`${value}T00:00:00`).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const toLocalDateKey = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const CompOffApplication = () => {
  const financialYear = useMemo(() => getCurrentFinancialYear(), []);

  const [balance, setBalance] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [reason, setReason] = useState('');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const [balanceResponse, leaveResponse] = await Promise.all([
        api.get('/employee/leave-balance'),
        api.get('/employee/leave'),
      ]);

      setBalance(balanceResponse.data);
      setRequests((leaveResponse.data.leaves || []).filter((leave) =>
        (leave.leaveDays || []).some((day) => (
          day.leaveType === 'comp_off'
          && toLocalDateKey(day.date) >= financialYear.firstDay
          && toLocalDateKey(day.date) <= financialYear.lastDay
        ))
      ));
    } catch (error) {
      console.error('Comp Off data error:', error);
      toast.error('Failed to load Comp Off details');
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // The current financial year is fixed for this page session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitCompOff = async (event) => {
    event.preventDefault();
    if (!selectedDate) {
      toast.error('Please select the day on which you want to use Comp Off');
      return;
    }
    if (balance?.comp_off_left <= 0) {
      toast.error(`No Comp Off balance is available for FY ${financialYear.label}`);
      return;
    }
    if (reason.trim().length < 10) {
      toast.error('Please provide a reason with at least 10 characters');
      return;
    }

    setSubmitting(true);
    try {
      const selectedDateTime = new Date(`${selectedDate}T00:00:00`).toISOString();
      await api.post('/employee/leave', {
        startDate: selectedDateTime,
        endDate: selectedDateTime,
        leaveDays: [{ date: selectedDateTime, dayType: 'full', leaveType: 'comp_off' }],
        reason: reason.trim(),
      });
      toast.success('Comp Off request submitted for admin approval');
      setSelectedDate('');
      setReason('');
      await fetchData(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit Comp Off request');
    } finally {
      setSubmitting(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await fetchData(false);
    setRefreshing(false);
  };

  const statusDetails = (status) => {
    if (status === 'approved') return { label: 'Approved', icon: CheckCircle, color: 'text-green-700 bg-green-50' };
    if (status === 'rejected') return { label: 'Rejected', icon: XCircle, color: 'text-red-700 bg-red-50' };
    return { label: 'Pending approval', icon: Clock, color: 'text-amber-700 bg-amber-50' };
  };

  if (loading) return <LoadingSpinner />;

  const available = Number(balance?.comp_off_left || 0);
  const earned = Number(balance?.comp_off_earned || 0);
  const used = Number(balance?.comp_off_used || 0);
  const eligibleDates = balance?.comp_off_dates || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
              <Gift className="h-6 w-6" />
            </span>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Comp Off</h1>
            <p className="text-gray-600">Use paid leave earned by working on a scheduled week-off or holiday during the financial year.</p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          className="inline-flex items-center justify-center gap-2 self-start rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <p className="text-sm font-medium text-amber-800">Available this financial year</p>
          <p className="mt-1 text-3xl font-bold text-amber-900">{available}</p>
          <p className="mt-1 text-xs text-amber-700">FY {financialYear.label}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-gray-500">Earned from attendance</p>
          <p className="mt-1 text-3xl font-bold text-gray-900">{earned}</p>
          <p className="mt-1 text-xs text-gray-500">Qualifying worked off-days</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-gray-500">Used or awaiting approval</p>
          <p className="mt-1 text-3xl font-bold text-gray-900">{used}</p>
          <p className="mt-1 text-xs text-gray-500">Pending requests reserve balance</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
        <div className="rounded-xl bg-white p-6 shadow">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Calendar className="h-5 w-5 text-amber-600" />
            Apply for Comp Off
          </h2>
          <div className="mt-4 rounded-lg border border-sky-100 bg-sky-50 p-3 text-sm text-sky-800">
            <Info className="mr-2 inline h-4 w-4" />
            Select a working day between {formatDate(financialYear.firstDay)} and {formatDate(financialYear.lastDay)}. This is a full-day paid leave and requires admin/HR approval.
          </div>

          {available > 0 ? (
            <form onSubmit={submitCompOff} className="mt-5 space-y-4">
              <div>
                <label htmlFor="comp-off-date" className="mb-1 block text-sm font-medium text-gray-700">Leave date</label>
                <input
                  id="comp-off-date"
                  type="date"
                  min={financialYear.firstDay}
                  max={financialYear.lastDay}
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100"
                  required
                />
              </div>
              <div>
                <label htmlFor="comp-off-reason" className="mb-1 block text-sm font-medium text-gray-700">Reason</label>
                <textarea
                  id="comp-off-reason"
                  rows="4"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Explain why you need this Comp Off"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
                {submitting ? 'Submitting...' : 'Submit Comp Off Request'}
              </button>
            </form>
          ) : (
            <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              <AlertCircle className="mr-2 inline h-4 w-4" />
              No Comp Off is currently available. Balance is earned after qualifying attendance is recorded and verified.
            </div>
          )}

          {eligibleDates.length > 0 && (
            <div className="mt-6 border-t border-gray-100 pt-4">
              <h3 className="text-sm font-semibold text-gray-900">Qualifying worked dates</h3>
              <div className="mt-2 flex flex-wrap gap-2">
                {eligibleDates.map((entry) => (
                  <span key={entry.date} className="rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
                    {formatDate(entry.date)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="rounded-xl bg-white p-6 shadow">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <FileText className="h-5 w-5 text-gray-600" />
            My Comp Off Requests
          </h2>
          {requests.length === 0 ? (
            <div className="mt-8 text-center text-sm text-gray-500">No Comp Off requests for FY {financialYear.label}.</div>
          ) : (
            <div className="mt-4 space-y-3">
              {requests.map((request) => {
                const details = statusDetails(request.status);
                const StatusIcon = details.icon;
                const requestDate = request.leaveDays?.find((day) => day.leaveType === 'comp_off')?.date || request.startDate;
                return (
                  <div key={request._id} className="rounded-lg border border-gray-200 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-900">{formatDate(requestDate)}</p>
                        <p className="mt-1 text-sm text-gray-600">{request.reason}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${details.color}`}>
                        <StatusIcon className="h-3.5 w-3.5" />
                        {details.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompOffApplication;
